import { useState } from "react";
import * as v from "valibot";
import { useLang } from "../../../services/lang";
import { useUser, UserInput } from "../../../components/User";
import { api, handleError } from "../../../services/api";
import { getLocalId } from "easy-analytics/client";
import { Checkbox, Input } from "../../../components/Form";
import { ArrowDown, Loading, VoteResult, When } from "../../../components";
import { Accordion } from "../../../components";
import { UserSchema } from "../../../services/checkData";
import { WhenClient, WhenVotes, Vote, VoteFull } from "../../../../types";

export function WhenVoting({ when, vote, onChange }: { when: WhenClient, vote: WhenVotes, onChange: (message: string) => void }) {
    const { dict } = useLang();
    const { user } = useUser();

    const [voteEdit, setVoteEdit] = useState<WhenVotes>(vote);
    const [message, setMessage] = useState("");
    const [isSending, setIsSending] = useState(false);

    function handleCheckboxChange(option: string, vote: Vote) {
        const voteFull = voteEdit[option] ? { ...voteEdit[option], vote } : { comment: "", vote };
        const newVote: WhenVotes = { ...voteEdit, [option]: voteFull };

        setVoteEdit(newVote);
    }

    async function handleSave() {
        if (!v.safeParse(UserSchema, user).success) {
            setMessage(dict.userNameMissing);
            return;
        }

        if (Object.keys(voteEdit).length !== Object.keys(when.options).length) {
            setMessage(dict.whenVoteMissing);
            return;
        }

        setIsSending(true);
        const [, error] = await api.updateWhenVote({
            whenId: when._id,
            vote: voteEdit,
            userId: getLocalId(),
            user,
        });
        setIsSending(false);

        if (error) {
            handleError(error);
            setMessage(dict.whenVoteError);
            return;
        }

        const message = dict.whenVoteSuccess;
        setMessage(message);
        onChange(message);
    }

    const sendMessage = !v.safeParse(UserSchema, user).success ? dict.userNameMissing
        : Object.keys(voteEdit).length !== Object.keys(when.options).length ? dict.whenVoteMissing
            : "";

    return <>
        <UserInput />

        <div className="vote-legend width-max">
            <div>
                <Checkbox value color="green" />
                {dict.whenLegendYes}
                <ArrowDown height={when.isMaybeOption ? 121 : 86} />
            </div>
            {when.isMaybeOption && <div>
                <Checkbox value color="yellow" />
                {dict.whenLegendMaybe}
                <ArrowDown height={86} />
            </div>}
            <div>
                <Checkbox value color="red" />
                {dict.whenLegendNo}
                <ArrowDown height={51} />
            </div>
            <div className="legend-comment">
                {dict.whenLegendComment}
                <ArrowDown height={16} />
            </div>
        </div>

        <ul className="poll-options when-options">
            {Object.keys(when.options).map(option => <li key={option} className={`when-vote-option`}>
                <Accordion openIconOnly title={<VoteResult
                    title={<>
                        <div className="when-vote">
                            <Checkbox value={voteEdit[option]?.vote === "yes"} color="green" onChange={() => handleCheckboxChange(option, "yes")} />
                            {when.isMaybeOption && <Checkbox value={voteEdit[option]?.vote === "maybe"} color="yellow" onChange={() => handleCheckboxChange(option, "maybe")} />}
                            <Checkbox value={voteEdit[option]?.vote === "no"} color="red" onChange={() => handleCheckboxChange(option, "no")} />
                            <div className="when-date">
                                <When date={option} />
                            </div>
                        </div>
                    </>}
                    yes={voteEdit[option]?.vote === "yes" ? 1 : 0}
                    maybe={voteEdit[option]?.vote === "maybe" ? 1 : 0}
                    no={voteEdit[option]?.vote === "no" ? 1 : 0}
                />} >
                    <Input
                        type="textarea"
                        className="vote-comment"
                        value={voteEdit[option] || { vote: "", comment: "" }}
                        field="comment"
                        setValue={(optionValue: VoteFull) => setVoteEdit({ ...voteEdit, [option]: optionValue })}
                        label={dict.pollVoteComment}
                        maxLength={400}
                    />
                </Accordion>
            </li>)}
        </ul>

        {message && <p className="alert">{message}</p>}

        {isSending && <Loading />}
        {!isSending && <>
            {sendMessage && <p className="info">{sendMessage}</p>}
            <button disabled={!!sendMessage} className="width" onClick={handleSave}>{dict.whenVoteSend}</button>
        </>}
    </>;
}
